import type { ReactNode } from "react";
import { SlideHeader } from "../../../SlideHeader";
import { SupLink } from "../../../SlideContext";

type StageAccent = "past" | "now" | "future";

const STAGES: Array<{
  label: string;
  body: ReactNode;
  foot: string;
  accent: StageAccent;
}> = [
  {
    label: "v1 — private alpha",
    body: "Two founders stood up the platform: validated the data-source ingestion, proved a rich ASO framework could run programmatically, shipped the first-generation UI.",
    foot: "Hypotheses validated.",
    accent: "past",
  },
  {
    label: "v2 — public beta",
    body: "Completed June '26 — core stabilized and extended on input from ASO experts, design partners, and beta users. Rich store analytics: in-store performance tracked against every published revision.",
    foot: "Live, with paying customers.",
    accent: "past",
  },
  {
    label: "Now — stabilize & sell",
    body: "Hardening v2 — audit quality, instrumentation, reliability — while GTM ramps through self-serve and the first agency relationships.",
    foot: "Self-serve → SMB → agencies → enterprise.",
    accent: "now",
  },
  {
    label: "Next — v3",
    body: "Extends the ASO core — custom product pages, in-app events, rich localization — and adds content generation at scale: listing text and imagery drafted across languages, audit recommendations materialized into a publishable revision. One click to ship.",
    foot: "Recommendations become content.",
    accent: "future",
  },
  {
    label: "Beyond — v4",
    body: "Full automation: switch on Apptonomy and watch constant ASO run itself — optimizations executed without a human approval step (keep one if you want). The system earns the trust to flip that switch.",
    foot: "Self-actuated, constant ASO.",
    accent: "future",
  },
  {
    label: "Long term",
    body: (
      <>
        The expansion play: the same data spine extended to UA creative
        briefs, attribution, and ad creative.
        <SupLink to="expansion-path">E</SupLink>
      </>
    ),
    foot: "Listing → ad → install → revenue.",
    accent: "future",
  },
];

export default function Roadmap() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8 md:[&_header]:mb-6">
      <SlideHeader eyebrow="Roadmap">
        <h1 className="text-3xl font-semibold leading-tight tracking-tight md:text-6xl md:leading-[1.1]">
          Closing the loop.
        </h1>
      </SlideHeader>

      {/* The trajectory — six chapters, stacked as a timeline */}
      <div className="flex flex-col gap-2 md:gap-2.5">
        {STAGES.map((s) => (
          <StageRow key={s.label} {...s} />
        ))}
      </div>

      {/* Closer */}
      <div className="mt-5 md:mt-auto">
        <div className="rounded-2xl bg-blue-50 px-5 py-4 ring-1 ring-blue-200 md:px-10 md:py-5">
          <p className="text-base italic leading-snug text-blue-900 md:text-xl">
            The destination is{" "}
            <span className="font-semibold not-italic">
              easy-to-use, fully autonomous, global ASO
            </span>{" "}
            — the first major component of a broader (proprietary)
            app-marketing operating system.
          </p>
        </div>
      </div>
    </div>
  );
}

function StageRow({
  label,
  body,
  foot,
  accent,
}: {
  label: string;
  body: ReactNode;
  foot: string;
  accent: StageAccent;
}) {
  const isNow = accent === "now";
  const isPast = accent === "past";
  return (
    <div
      className={`flex flex-col gap-1.5 rounded-xl px-4 py-3 ring-1 md:flex-row md:items-baseline md:gap-5 md:px-5 ${
        isNow
          ? "bg-blue-50 ring-blue-300"
          : isPast
            ? "bg-slate-50 ring-slate-200"
            : "bg-white ring-slate-200"
      }`}
    >
      <div className="flex shrink-0 items-center gap-2 md:w-52">
        <p
          className={`text-xs font-bold uppercase tracking-[0.12em] md:text-sm ${
            isNow ? "text-blue-700" : isPast ? "text-slate-500" : "text-slate-700"
          }`}
        >
          {label}
        </p>
      </div>
      <p className="flex-1 text-xs leading-snug text-slate-700 md:text-sm">
        {body}
      </p>
      <p
        className={`shrink-0 text-xs italic leading-snug md:w-64 md:text-right md:text-sm ${
          isNow ? "text-blue-800" : "text-slate-500"
        }`}
      >
        {foot}
      </p>
    </div>
  );
}
